import { ApiError } from '@/api/errors'
import { env } from '@/lib/env'
import type { ApiErrorBody } from '@/types/api'

const TIMEOUT_MS = 20_000

/** Spring Security's defaults, and what `CookieCsrfTokenRepository` writes. */
const CSRF_COOKIE = 'XSRF-TOKEN'
const CSRF_HEADER = 'X-XSRF-TOKEN'

/** Methods the server treats as safe; they need no CSRF token. */
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export interface RequestOptions {
  /** Appended as a query string; `undefined` and `null` values are dropped. */
  params?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
  headers?: Record<string, string>
}

/**
 * What the client does when the server says the session is gone. Set once at
 * startup by the auth feature, which keeps this module free of feature imports.
 */
let onUnauthenticated: () => void = () => {}

export function configureApiAuth(hooks: { onUnauthenticated: () => void }): void {
  onUnauthenticated = hooks.onUnauthenticated
}

/**
 * The CSRF token is deliberately readable — the server sets that cookie without
 * `HttpOnly` precisely so this can echo it back. Missing simply means no cookie
 * has been issued yet, which is the case before the first response.
 */
function csrfToken(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${CSRF_COOKIE}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

function buildUrl(path: string, params: RequestOptions['params']): string {
  const url = `${env.apiBaseUrl}${path}`
  if (!params) return url

  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) query.append(key, String(value))
  }
  const search = query.toString()
  return search ? `${url}?${search}` : url
}

/** A JSON body if there is one, `null` when the response is empty or not JSON. */
async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null
  const type = response.headers.get('content-type') ?? ''
  if (!type.includes('json')) return null

  try {
    return await response.json()
  } catch {
    // A truncated or malformed body is not worth failing twice over; the
    // status line already says what happened.
    return null
  }
}

async function request<T>(
  method: Method,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers }

  // The logo upload sends a `Blob` straight through; the server reads the
  // image type from the bytes, so a `Content-Type` here is only a hint.
  const isBinary = body instanceof Blob
  const isForm = body instanceof URLSearchParams
  if (body !== undefined && !isBinary)
    headers['Content-Type'] = isForm ? 'application/x-www-form-urlencoded' : 'application/json'
  if (isBinary && body.type) headers['Content-Type'] = body.type

  if (!SAFE_METHODS.has(method)) {
    const token = csrfToken()
    if (token) headers[CSRF_HEADER] = token
  }

  // The caller's own signal still wins; this only adds the deadline.
  const timeout = AbortSignal.timeout(TIMEOUT_MS)
  const signal = options.signal ? AbortSignal.any([options.signal, timeout]) : timeout

  let response: Response
  try {
    response = await fetch(buildUrl(path, options.params), {
      method,
      headers,
      // The session cookie is the credential; without this it is not sent.
      credentials: 'include',
      body:
        body === undefined
          ? undefined
          : isBinary
            ? body
            : isForm
              ? body.toString()
              : JSON.stringify(body),
      signal,
    })
  } catch (error) {
    // `fetch` rejects only for network-level failures and aborts — never for
    // an HTTP error status, which is why those are handled below instead.
    throw ApiError.from(error)
  }

  if (!response.ok) {
    const problem = (await readBody(response)) as ApiErrorBody | null
    if (response.status === 401) onUnauthenticated()
    throw ApiError.fromResponse(response.status, problem)
  }

  return (await readBody(response)) as T
}

/** Thin typed helpers so features read `api.get<Post>(...)` instead of unwrapping. */
export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, body, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, body, options),
  /** Raw bytes (the logo); the server reads the type from the bytes, so the header is a hint. */
  putBinary: <T>(path: string, body: Blob, options?: RequestOptions) =>
    request<T>('PUT', path, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, body, options),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>('DELETE', path, undefined, options),
}
